import { useState } from "react";
import type { FormEvent } from "react";

import { useDialogForm, useToast } from "@/ds";
import { atualizarProjecaoFinanceira, criarProjecaoFinanceira } from "@/modules/financeiro-obras";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { useDetalheEscopoMacroDados } from "../../../../hooks/use-planejamento-dados";
import { JUSTIFICATIVA_OBRIGATORIA } from "@/shared/components";
import { FEL_ZERO } from "../planejamento-controle.cancelamento";
import { useProjecaoFormulario } from "../projecao-formulario/use-projecao-formulario";
import {
  AVISO_APROVACAO,
  PROJECAO_ATUALIZADA_MESSAGE,
  PROJECAO_ENVIADA_APROVACAO,
  PROJECAO_FORM_ID,
  PROJECAO_TITLE,
  PROJECAO_VIGENTE_MESSAGE,
  SALVAR_SEM_ALTERACAO,
} from "./projecao-dialog.constants";
import type { ProjecaoDialogProps } from "./types-projecao-dialog";

export function useProjecaoDialog({
  obraId,
  modo,
  escopo,
  obra,
  cotacaoDaObra,
  onClose,
  onCotacaoAlterada,
  onConcluido,
}: ProjecaoDialogProps) {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const editando = modo === "edicao";
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const consultaDetalhe = useDetalheEscopoMacroDados(obraId, escopo.obraEscopoMacroId, editando);
  const detalhe = editando ? consultaDetalhe.data : null;
  const vigente = detalhe?.projecao ?? null;

  const {
    corpo,
    concluidoValido,
    completo,
    materiaisIncompleto: _materiaisIncompleto,
    cotacaoAlterada,
    isDirty: formularioSujo,
    consultaOpcoes,
    descartar: _descartar,
    editarCotacao: _editarCotacao,
    ...formulario
  } = useProjecaoFormulario({
    obraId,
    alvo: {
      obraTipoObraId: escopo.obraTipoObraId,
      ...(editando ? { obraEscopoMacroId: escopo.obraEscopoMacroId } : {}),
    },
    obra,
    cotacaoDaObra,
    classificacaoEscopo: escopo.classificacao,
    vigente,
    totalVigente: detalhe?.totais?.total,
    previaAposMudanca: editando,
    mostrarObrigatorios: tentouSalvar,
  });

  const consultas = editando ? [consultaOpcoes, consultaDetalhe] : [consultaOpcoes];
  const falha = consultas.find((c) => c.status === "error" || c.status === "forbidden");
  const status = falha
    ? falha.status
    : consultas.every((c) => c.status === "success")
      ? "success"
      : "loading";
  const errorMessage = falha?.errorMessage ?? null;
  const recarregar = () => {
    consultaOpcoes.recarregar();
    if (editando) consultaDetalhe.reload();
  };
  const exigeJustificativa = editando && !!detalhe && detalhe.obra.fel !== FEL_ZERO;
  const pronto = status === "success";

  const avisarCotacao = () => {
    if (cotacaoAlterada) onCotacaoAlterada?.();
  };
  const fechar = () => {
    avisarCotacao();
    onClose();
  };

  const form = useDialogForm({
    initial: { justificativa: "" },
    validate: ({ justificativa }) =>
      exigeJustificativa && justificativa.trim() === ""
        ? { justificativa: JUSTIFICATIVA_OBRIGATORIA }
        : {},
    onSubmit: async () => {
      if (!corpo || !salvarHabilitado) return;
      const justificativa = form.values.justificativa.trim();
      const body = exigeJustificativa ? { ...corpo, justificativa } : corpo;
      const gravar = editando ? atualizarProjecaoFinanceira : criarProjecaoFinanceira;
      const gravada = await comChave(body, (idempotencyKey) =>
        gravar(obraId, escopo.obraEscopoMacroId, body, { idempotencyKey }),
      );
      const efetivada = gravada.situacao === "VIGENTE";
      toast.success(
        !efetivada
          ? PROJECAO_ENVIADA_APROVACAO
          : editando
            ? PROJECAO_ATUALIZADA_MESSAGE
            : PROJECAO_VIGENTE_MESSAGE,
      );
      avisarCotacao();
      onConcluido();
    },
  });
  const isDirty = formularioSujo || form.isDirty;
  const descarte = useConfirmarDescarte({ onDescartar: fechar, testId: "projecao-descarte" });

  const faltaJustificativa = exigeJustificativa && form.values.justificativa.trim() === "";
  const semAlteracao = editando && !isDirty;
  const salvarHabilitado = pronto && concluidoValido && !faltaJustificativa && !semAlteracao;
  let salvarAviso: string | null = null;
  if (!salvarHabilitado && completo && semAlteracao) salvarAviso = SALVAR_SEM_ALTERACAO;

  return {
    formId: PROJECAO_FORM_ID,
    titulo: PROJECAO_TITLE,
    estado: status,
    errorMessage,
    recarregar,
    escopo,
    tipoObraCodigo: escopo.tipoObra.split(" · ")[0] ?? "",
    classificacaoRotulo: formulario.classificacao
      ? (formulario.classificacao.opcoes.find(
          (opcao) => opcao.value === formulario.classificacao?.valor,
        )?.label ?? formulario.classificacao.pendente)
      : null,
    obra,
    ...formulario,
    justificativa: exigeJustificativa
      ? {
          valor: form.values.justificativa,
          digitar: (texto: string) => form.setValue("justificativa", texto),
          aviso: AVISO_APROVACAO,
          erro: form.errors.justificativa ?? null,
        }
      : null,
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    salvarDesabilitado: !pronto,
    salvarAviso: tentouSalvar ? salvarAviso : null,
    enviar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setTentouSalvar(true);
      void form.submit();
    },
    cancelar: () => descarte.pedir(isDirty),
    descarte: descarte.confirmacao,
  };
}
